'use strict';

/**
 * T451 P3.4b — the browser-sealed tasks WRITE path as ONE shared writer.
 *
 * task-lists.js (P3.4) proved the shape: after a sealed read
 * (TASKS_BROWSER_DECRYPT=1) the view holds the FULL store the vault bundle
 * unsealed plus the server-declared scope + etag; every mutation is applied in
 * the page with the SAME shared logic the server runs
 * (OysterVault.taskMutations), the whole store is re-sealed under the session
 * master key (OysterVault.sealTaskStore) and PUT verbatim to
 * /api/task-lists/sealed — the server never needs the key. The sibling views
 * (routines-view.js, code-workspace-view.js) need exactly that machinery, and
 * a third and fourth copy of it would be the "repeated convention" a security
 * property must not rest on. This module is that machinery, delivered on the
 * vault bundle as OysterVault.createSealedTaskWriter.
 *
 * Contract (every outcome typed; nothing here throws after construction):
 *   unseal(data)   → { ok:true, state, updatedAt, userUpdatedAt }
 *                  | { ok:false, code:'SEALED_TASKS_LOCKED', unlock:true, error }
 *                  | { ok:false, code:'SEALED_TASKS_NEED_VAULT_BUNDLE' | 'SEALED_TASKS_<status>' }
 *   mutate(fn)     → { ok:true, result, written:Promise } — fn(M, ctx) runs the
 *                    shared mutation against ctx.store; its return value is
 *                    `result`; `written` settles when the PUT has settled
 *                  | { ok:false, code:'SEALED_TASKS_MUTATION_FAILED' } (a thrown
 *                    mutation ⇒ reload(), nothing written)
 *                  | { ok:false, code:'SEALED_TASKS_NEED_VAULT_BUNDLE' }
 *   hasCtx() / etag() / clear() / requestReunlock()
 *
 * Callbacks: onState(state) after every local apply (the view re-renders from
 * the server's own normalizer); onError({ phase:'read'|'write', code, detail })
 * for a typed failure the view should name; reload() when the local store can
 * no longer be trusted (conflict, thrown mutation, cancelled unlock, locked
 * read after vault-ready).
 *
 * Flag OFF ⇒ the read carries no `sealed` field ⇒ the view never calls
 * unseal(); hasCtx() stays false and every write takes the warm route exactly
 * as before (the proven-path twin).
 */

function toCode(prefix, status) {
  return prefix + String(status || 'unknown').toUpperCase().replace(/[^A-Z0-9]+/g, '_');
}

function createSealedTaskWriter(opts) {
  const o = opts || {};
  if (typeof o.apiFetch !== 'function') throw new Error('createSealedTaskWriter: apiFetch (the view\'s JSON transport) is required');
  const apiFetch = o.apiFetch;
  const reason = o.reason || 'tasks';
  const resolveVault = typeof o.resolveVault === 'function' ? o.resolveVault : () => (typeof window !== 'undefined' ? window.OysterVault : null);
  const resolveReunlock = typeof o.resolveReunlock === 'function' ? o.resolveReunlock : () => (typeof window !== 'undefined' ? window.OysterVaultReunlock : null);
  const resolveDocument = typeof o.resolveDocument === 'function' ? o.resolveDocument : () => (typeof document !== 'undefined' ? document : null);
  const onState = typeof o.onState === 'function' ? o.onState : () => {};
  const onError = typeof o.onError === 'function' ? o.onError : () => {};
  const reload = typeof o.reload === 'function' ? o.reload : () => {};

  let ctx = null; // { store, ownerKey, fallbackKeys, recordId, etag }
  let queue = Promise.resolve(); // writes serialize: each seals the CURRENT store with the CURRENT etag
  let reloadArmed = false;

  function vault() { try { return resolveVault() || null; } catch (_e) { return null; } }
  function pieces(v) {
    return !!(v && typeof v.unsealTaskStore === 'function' && typeof v.sealTaskStore === 'function'
      && v.taskMutations && typeof v.normalizeTaskStateForUser === 'function');
  }

  // One armed re-load per lock, however many times a locked read repeats.
  function armReloadOnVaultReady() {
    if (reloadArmed) return;
    const doc = resolveDocument();
    if (!doc || typeof doc.addEventListener !== 'function') return;
    reloadArmed = true;
    try { doc.addEventListener('vault-ready', function () { reloadArmed = false; reload(); }, { once: true }); }
    catch (_e) { reloadArmed = false; }
  }

  function unseal(data) {
    const v = vault();
    if (!v || typeof v.unsealTaskStore !== 'function') {
      ctx = null;
      onError({ phase: 'read', code: 'SEALED_TASKS_NEED_VAULT_BUNDLE' });
      return { ok: false, code: 'SEALED_TASKS_NEED_VAULT_BUNDLE' };
    }
    const r = v.unsealTaskStore(data.envelope, { ownerKey: data.ownerKey, fallbackKeys: data.fallbackKeys });
    if (r && (r.status === 'ok' || r.status === 'absent')) {
      ctx = {
        store: r.store, ownerKey: data.ownerKey, fallbackKeys: data.fallbackKeys || [],
        recordId: data.recordId, etag: data.etag === undefined ? null : data.etag,
      };
      return { ok: true, state: r.state, updatedAt: r.updatedAt || null, userUpdatedAt: (r.state && r.state.updatedAt) || null };
    }
    ctx = null;
    if (r && r.status === 'locked') {
      // The ORDINARY post-login state (the handoff carries no master key by
      // design): re-load once on the next vault-ready.
      armReloadOnVaultReady();
      return { ok: false, code: 'SEALED_TASKS_LOCKED', error: 'Unlock your vault to read your tasks', unlock: true };
    }
    const code = toCode('SEALED_TASKS_', r && r.status);
    onError({ phase: 'read', code });
    return { ok: false, code };
  }

  // The ONE way a view asks for the master key: the shared chrome-level
  // re-unlock surface, never a password field inside module content.
  function requestReunlock() {
    const s = resolveReunlock();
    if (!s || typeof s.open !== 'function') {
      onError({ phase: 'read', code: 'SEALED_TASKS_REUNLOCK_SURFACE_MISSING' });
      return false;
    }
    armReloadOnVaultReady();
    try { s.open({ reason }); } catch (_e) { onError({ phase: 'read', code: 'SEALED_TASKS_REUNLOCK_SURFACE_FAILED' }); return false; }
    return true;
  }

  function mutate(fn) {
    const v = vault();
    if (!ctx || !pieces(v)) {
      onError({ phase: 'write', code: 'SEALED_TASKS_NEED_VAULT_BUNDLE' });
      return { ok: false, code: 'SEALED_TASKS_NEED_VAULT_BUNDLE' };
    }
    const s = ctx;
    let result;
    // A thrown mutation mirrors the warm path's catch: re-load, never a
    // half-applied local state.
    try { result = fn(v.taskMutations, s); } catch (_e) { reload(); return { ok: false, code: 'SEALED_TASKS_MUTATION_FAILED' }; }
    onState(v.normalizeTaskStateForUser(s.store, s.ownerKey, { fallbackKeys: s.fallbackKeys }));
    queue = queue.then(writeNow, writeNow);
    return { ok: true, result, written: queue };
  }

  // Seal the CURRENT store under the session key and PUT it with the CURRENT
  // etag. `locked` opens the re-unlock surface DIRECTLY and retries on
  // vault-ready; it must NOT arm the read re-load (that would replace the store
  // carrying the pending mutation). A conflict re-loads: nothing is dropped
  // silently.
  function writeNow() {
    const s = ctx;
    if (!s) return Promise.resolve();
    const v = vault();
    const r = v && typeof v.sealTaskStore === 'function'
      ? v.sealTaskStore(s.store, { recordId: s.recordId, ownerKey: s.ownerKey, fallbackKeys: s.fallbackKeys })
      : { status: 'need-vault-bundle' };
    if (!r || r.status !== 'ok') {
      if (r && r.status === 'locked') return writeAfterReunlock();
      onError({ phase: 'write', code: toCode('SEALED_TASKS_', r && r.status) });
      return Promise.resolve();
    }
    return apiFetch('/api/task-lists/sealed', { method: 'PUT', body: JSON.stringify({ envelope: r.envelope, ifMatch: s.etag }) })
      .then(function (d) { return settle(s, d); })
      .catch(function (err) {
        // T451 P3.4c — a transport that REJECTS a refusal (routines-view's
        // apiFetch rejects every non-2xx with the parsed body on `.payload`,
        // T320 P2) is a REFUSAL, not a transport failure: settle it like a
        // resolved body. Read as a failure it re-loaded and the user's change
        // vanished unnamed. Only a rejection with NO parsed body re-loads.
        const d = err && err.payload && typeof err.payload === 'object' ? err.payload : null;
        if (!d) { reload(); return undefined; }
        return settle(s, d);
      });
  }

  function settle(s, d) {
    if (d && d.ok === true && d.sealed === true) { if (ctx === s) s.etag = d.etag; return d; }
    if (d && d.code === 'TASKS_SEALED_WRITE_CONFLICT') { reload(); return d; }
    onError({ phase: 'write', code: (d && (d.code || d.error)) || 'SEALED_TASKS_WRITE_FAILED', detail: d });
    return d;
  }

  function writeAfterReunlock() {
    const surface = resolveReunlock();
    const doc = resolveDocument();
    if (!surface || typeof surface.open !== 'function' || !doc || typeof doc.addEventListener !== 'function') {
      onError({ phase: 'write', code: 'SEALED_TASKS_REUNLOCK_SURFACE_MISSING' });
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      let settled = false;
      const onReady = function () {
        if (settled) return;
        settled = true;
        writeNow().then(resolve, resolve);
      };
      try { doc.addEventListener('vault-ready', onReady, { once: true }); } catch (_e) { /* no document */ }
      let opened;
      try { opened = surface.open({ reason }); } catch (_e) { opened = Promise.resolve('cancelled'); }
      Promise.resolve(opened).then(function (outcome) {
        if (outcome === 'cancelled' && !settled) {
          settled = true;
          try { doc.removeEventListener('vault-ready', onReady); } catch (_e) { /* best effort */ }
          reload(); // the unsaved change is dropped honestly, never kept as if saved
          resolve();
        }
      }, function () { /* the dialog's own failure is shown in the dialog */ });
    });
  }

  return {
    unseal,
    mutate,
    requestReunlock,
    hasCtx: function () { return !!ctx; },
    etag: function () { return ctx ? ctx.etag : null; },
    clear: function () { ctx = null; },
  };
}

module.exports = { createSealedTaskWriter };
